"use client";

import React, { useEffect, useRef, useState } from "react";
import { Cpu, Users, Wrench, Terminal } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const teamData = [{
  id: "LEAD-01",
  name: "Sarah Jenkins",
  role: "Registered Manager",
  subRole: "Clinical Operations Lead",
  bio: "With over 15 years in healthcare management, Sarah ensures our care standards exceed CQC requirements every day.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1665643/img/sarah_manager_portrait.png",
  icon: Users,
  skills: [{
    name: "Care Compliance",
    value: 98
  }, {
    name: "Team Leadership",
    value: 95
  }, {
    name: "Patient Advocacy",
    value: 92
  }]
}, {
  id: "LEAD-02",
  name: "Dr. Michael Chen",
  role: "Clinical Director",
  subRole: "Medical Strategy Advisor",
  bio: "Michael oversees our clinical protocols, bringing a wealth of experience in elderly care and hospital discharge pathways.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1665643/img/michael_director_portrait.png",
  icon: Cpu,
  skills: [{
    name: "Clinical Strategy",
    value: 96
  }, {
    name: "Elderly Care",
    value: 94
  }, {
    name: "Risk Management",
    value: 90
  }]
}, {
  id: "LEAD-03",
  name: "Emma Thompson",
  role: "Care Coordinator",
  subRole: "Family Liaison Specialist",
  bio: "Emma bridges the gap between families and caregivers, ensuring personalized care plans that truly reflect individual needs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1665643/img/emma_coordinator_portrait.png",
  icon: Wrench,
  skills: [{
    name: "Care Planning",
    value: 95
  }, {
    name: "Communication",
    value: 97
  }, {
    name: "Crisis Support",
    value: 88
  }]
}];
export default function NosPiliers() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return;
}
function TeamMemberCard({
  member,
  index
}: {
  member: (typeof teamData)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const Icon = member.icon;
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="group relative h-full overflow-hidden border-border bg-card shadow-md transition-all duration-300 hover:shadow-xl hover:border-primary/30 rounded-lg">
        {}
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
          <div className="absolute inset-0 z-10 bg-gradient-to-t from-card via-transparent to-transparent opacity-60" />
          <img src={member.image} alt={`Portrait of ${member.name}`} className="h-full w-full object-cover object-center grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
          
          {}
          <div className="absolute left-4 top-4 z-20 rounded-md bg-background/90 px-3 py-1 font-mono text-xs font-bold text-primary shadow-sm backdrop-blur-sm">
            {member.id}
          </div>
        </div>

        <CardHeader className="relative z-20 -mt-16 pb-2">
          <div className="mb-2 flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20">
              <Icon className="size-5" />
            </div>
            <div>
              <h3 className="font-default text-2xl font-bold text-foreground">
                {member.name}
              </h3>
              <p className="font-default text-xs font-semibold text-primary uppercase tracking-wider">
                {member.role}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="relative z-20 flex flex-col gap-6 pt-4">
          <div className="space-y-2">
            <p className="font-mono text-xs text-muted-foreground">
              <span className="text-foreground/60">SPECIALISM:</span>{" "}
              {member.subRole}
            </p>
            <p className="font-default text-sm text-muted-foreground leading-relaxed">
              {member.bio}
            </p>
          </div>

          {}
          <div className="mt-auto space-y-4 border-t border-border pt-4">
            <p className="font-default text-xs font-bold text-foreground/80 uppercase tracking-widest">
              Expertise Metrics
            </p>
            <div className="space-y-3">
              {member.skills.map((skill, skillIndex) => <div key={skillIndex} data-index={skillIndex} className="space-y-1.5">
                  <div className="flex justify-between font-default text-xs">
                    <span className="text-muted-foreground font-medium">{skill.name}</span>
                    <span className="text-primary font-bold">{skill.value}%</span>
                  </div>
                  <Progress value={skill.value} className="h-1.5 bg-muted rounded-lg" />
                </div>)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}